import { DataTable } from '@/components/DataTable/DataTable';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Button, Tag, AvatarStack, PersonAvatar } from '@teo/components';
import { axios } from '@/axios';
import { config } from '@/config';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { newUid, pastels } from '@/util';
import { useMediaQuery } from 'react-responsive';
import ReactDOM from 'react-dom';

const HoverDropdownWrapper = ({ content, dropdown }: any) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({
    top: 0,
    bottom: 0,
    left: 0,
    width: 0,
    height: 0,
    isBottom: false,
  });

  useEffect(() => {
    if (visible && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const bodyHeight = document.body.scrollHeight;
      const isBottom = bodyHeight - (rect.bottom + window.scrollY) < 350;
      setPosition({
        top: rect.top + window.scrollY,
        bottom: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height,
        isBottom: isBottom,
      });
    }
  }, [visible]);

  return (
    <>
      <div
        ref={anchorRef}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        className="relative inline-block"
      >
        {content}
      </div>

      {visible &&
        ReactDOM.createPortal(
          <div
            onMouseLeave={() => setVisible(false)}
            style={{
              top: position.isBottom ? position.bottom : position.top,
              left: position.left,
              zIndex: 9999,
              position: 'absolute',
              transform: position.isBottom
                ? 'translateY(-100%)'
                : 'translateY(-10px)',
              marginLeft: position.width + 4,
            }}
            className="absolute mt-2 min-w-60 divide-y divide-solid divide-grey-02 rounded-lg bg-white drop-shadow-m"
          >
            {dropdown}
          </div>,
          document.body
        )}
    </>
  );
};

export const CourseTable = (props: any) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ query: '(min-width: 1200px)' });

  const goRouteId = (url: any) => {
    navigate(url);
  };

  return (
    <DataTable
      key={props.refetchTable}
      defaultSort={[
        { id: 'courseStatus', desc: false },
        { id: 'updatedAt', desc: false },
        { id: 'name', desc: false },
      ]}
      columns={[
        {
          title:
            props?.courseType === 'assessment'
              ? t('columns.assessmentName')
              : props?.courseType === 'leervacature'
              ? t('columns.vacancyName')
              : t('columns.courseName'),
          id: 'name',
          className: 'col-start-1 col-end-8 row-start-1',
          render: (course: any) => (
            <div className="flex items-center">
              <div className="mr-3 w-16 min-w-16 sm:w-20 sm:min-w-20">
                <img
                  src={
                    course?.courseImage ||
                    course?.image ||
                    '/images/courseFallback.png'
                  }
                  alt={course.name}
                  className="inline h-12 w-full rounded-lg object-cover sm:h-14"
                />
              </div>
              {course?.courseName || course?.name}
            </div>
          ),
        },
        {
          addTitleOnMobile: true,
          id: 'version',
          title: t('columns.version'),
          className: 'col-start-9 col-end-10 row-start-1  justify-end',
          align: 'right',
        },
        {
          addTitleOnMobile: true,
          id: 'nrOfStudents',
          title: t('columns.nrOfStudents'),
          className:
            'col-start-4 col-end-7 row-start-2 justify-center text-center',
          align: 'right',
        },
        {
          addTitleOnMobile: true,
          id: 'nrOfTeams',
          title: t('columns.teams'),
          className: 'col-start-1 col-end-4 row-start-2',
          align: 'right',
          sortable: false,
          render: (course: any) => (
            <>
              {course?.teams?.length > 0 ? (
                <div className="group relative inline-block">
                  <HoverDropdownWrapper
                    key={course?.id}
                    content={
                      <AvatarStack>
                        {course?.teams?.map((item: any, i: any) => (
                          <PersonAvatar
                            key={item}
                            id={item}
                            name={course?.teamNames[i]}
                            bgColor={
                              course?.teamColors[i]
                                ? course?.teamColors[i]
                                : pastels[i % pastels.length]
                            }
                          />
                        ))}
                      </AvatarStack>
                    }
                    dropdown={
                      <div className=" flex flex-col gap-3 px-4 py-3">
                        {course?.teams?.map((item: any, i: any) => (
                          <div key={item} className="flex items-center gap-3">
                            <PersonAvatar
                              id={item}
                              name={course?.teamNames[i]}
                              bgColor={
                                course?.teamColors[i]
                                  ? course?.teamColors[i]
                                  : pastels[i % pastels.length]
                              }
                            ></PersonAvatar>
                            <p className="text-[12px] font-medium text-[#231f20] sm:text-sm">
                              {course?.teamNames[i]}
                            </p>
                          </div>
                        ))}
                      </div>
                    }
                  />
                </div>
              ) : (
                0
              )}
            </>
          ),
        },
        {
          addTitleOnMobile: true,
          className: 'col-start-8 col-end-10 row-start-2 justify-end',
          id: 'createdAt',
          title: t('columns.created'),
          render: (course: any) =>
            format(new Date(course.createdAt), 'dd/MM/yyyy'),
          align: 'right',
        },
        {
          addTitleOnMobile: true,
          id: 'courseStatus',
          title: t('columns.status'),
          className: 'col-start-1 col-end-4 row-start-3',
          align: 'right',
          render: (course: any) => (
            <Tag
              title={t(course.courseStatus)}
              variant={course.courseStatus === 'active' ? 'success' : 'default'}
            />
          ),
        },
        {
          addTitleOnMobile: true,
          id: 'closeTeam',
          title: '',
          className: 'col-start-5 col-end-10 row-start-3 justify-end',
          align: 'right',
          render: (course: any) => (
            <>
              {isDesktop ? (
                course?.courseStatus !== 'disabled' && (
                  <Button
                    variant="destruct"
                    size="md"
                    className="flex items-center"
                    onClick={async (event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      const result = await axios
                        .put(`${config.backend}/course/${course.id}`, {
                          courseStatus: 'disabled',
                        })
                        .then(() => props.setRefetchTable(newUid(20)));
                      props.setCourse(Object.assign({}, course, result?.data));
                    }}
                  >
                    {course?.courseType == 'assessment'
                      ? t('pages.follow_up_teams.close_assessment')
                      : course?.courseType == 'leervacature'
                      ? t('pages.follow_up_teams.close_vacancy')
                      : course?.courseType == 'instruction'
                      ? t('pages.follow_up_teams.close_instruction')
                      : t('pages.follow_up_teams.close_course')}
                  </Button>
                )
              ) : (
                <Button
                  className="relative z-[2] flex items-center"
                  variant="outline"
                  size="sm"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const rowPath = props?.rowPath?.(course);
                    goRouteId(rowPath);
                    window.scrollTo(0, 0);
                  }}
                >
                  {t('pages.follow_up.to_details')}
                </Button>
              )}
            </>
          ),
        },
        // {
        /*
          id: 'actionButton',
          className: 'col-start-9 row-start-1 justify-end',
          sortable: false,
          render: (course: any) => (
            <OptionsAction
              menuItems={[
                {
                  addIcon: true,
                  id: '1',
                  title: t('pages.follow_up.go_to_career_course'),
                  onClick: (ev) => {
                    navigate(`/${i18n.language}/create/${organisationId}/${props.courseType}s/${course?.id}`)
                  },
                },
                // {
                //   id: '2',
                //   title: t('pages.follow_up.follow_courses'),
                // },
                {
                  addOnMobile: true,
                  itemDelete: true,
                  id: '3',
                  onClick: (ev) => {
                    await axios.delete(`${config.backend}/courseteam/${course?.id}/${usr?.userId}/`);

                    /courseteam/:courseId/:teamId

                    setResetUserTable(newUid(20));
                  },
                  title: t('pages.follow_up.delete_course'),
                },
              ]}
              pageDetail={{
                title: t('pages.follow_up.go_to_career_course'),
                rowPath: (course) => `${basePath}/${course.id}`,
              }}
              rowInfo={course}
              mobileHeader={{
                render: (course: any) => (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div className="mr-3 w-16 min-w-16 sm:w-20 sm:min-w-20">
                      <img
                        src={course.courseImage || '/images/courseFallback.png'}
                        alt={course.name}
                        className="inline h-12 w-full rounded-lg object-cover sm:h-14"
                      />
                    </div>
                    {course.name}
                  </div>
                ),
              }}
            />
          ),
        */
        // },
      ]}
      {...props}
    />
  );
};
